import { Box, Flex, SegmentedControl } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { MachineCameraStreamViewer } from 'components/machine/camera-stream-viewer';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { VisionCalibrationCameraType } from 'lib_ts/enums/vision.enums';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

interface IProps {
  cameraType: VisionCalibrationCameraType;
  hideStreamFailed?: boolean;
  onChangeCameraType?: (source: VisionCalibrationCameraType) => void;
}

export const RemoteCalibrationStream = ({
  cameraType,
  hideStreamFailed,
  onChangeCameraType,
}: IProps) => {
  const machine = useMachinesPortalStore((s) => s.machine);

  if (!machine) {
    return null;
  }

  const { machineID } = machine;

  return (
    <ErrorBoundary identifier="RemoteCalibrationStream">
      <Flex className="remote-calibration-stream" position="relative">
        <TransformWrapper
          // how fast to zoom
          wheel={{
            step: 80,
            smoothStep: 0.02,
          }}
          panning={{
            wheelPanning: false, // so scrolling on trackpad doesn't pan around the image
          }}
          centerZoomedOut // keep image centered when zoomed all the way out so users don't see empty space
        >
          <TransformComponent wrapperClass="cursor-zoom-in">
            <MachineCameraStreamViewer
              machineID={machineID}
              splitViewSide={
                cameraType === VisionCalibrationCameraType.leader
                  ? 'left'
                  : 'right'
              }
              hideStreamFailed={hideStreamFailed}
              hideRefreshRate
              hideSource
            />
          </TransformComponent>
        </TransformWrapper>
        {onChangeCameraType && (
          <Box
            position="absolute"
            bottom="8px"
            right="8px"
            style={{ zIndex: 1 }}
          >
            <SegmentedControl.Root
              value={cameraType}
              style={{ background: 'var(--gray-3)' }}
              onValueChange={(v) => {
                onChangeCameraType(v as VisionCalibrationCameraType);
              }}
              size="1"
            >
              <SegmentedControl.Item
                value={VisionCalibrationCameraType.leader}
                className="cursor-pointer"
              >
                Leader
              </SegmentedControl.Item>
              <SegmentedControl.Item
                value={VisionCalibrationCameraType.follower}
                className="cursor-pointer"
              >
                Follower
              </SegmentedControl.Item>
            </SegmentedControl.Root>
          </Box>
        )}
      </Flex>
    </ErrorBoundary>
  );
};
